import { useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { getRecaptchaToken } from "@/lib/recaptcha";
import { GoldButton } from "./Ui";
import { CheckCircle2 } from "lucide-react";

// fields: [{name,label,type,options?,required?,full?,placeholder?}]
export default function EnquiryForm({ type, fields, submitLabel = "Send Enquiry", successMsg, testid = "enquiry-form" }) {
  const { settings } = useSite();
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [hp, setHp] = useState("");

  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const base = fields.reduce((acc, f) => {
        if (["name", "email", "whatsapp"].includes(f.name)) acc[f.name] = values[f.name] || "";
        return acc;
      }, {});
      const data = {};
      fields.forEach((f) => {
        if (!["name", "email", "whatsapp"].includes(f.name)) data[f.label] = values[f.name] || "";
      });
      const recaptcha_token = await getRecaptchaToken(settings.recaptcha_site_key, "enquiry");
      await api.post("/enquiry", { type, ...base, data, hp, recaptcha_token });
      setDone(true);
      toast.success("Sent! We'll be in touch soon.");
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Could not send. Try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done)
    return (
      <div data-testid={`${testid}-success`} className="card-surface rounded-2xl p-8 text-center gold-glow">
        <CheckCircle2 className="mx-auto text-[#D4AF37]" size={44} />
        <h3 className="mt-4 text-xl font-display">Thank you!</h3>
        <p className="mt-2 text-white/70 text-sm">{successMsg || "We've received your enquiry and will reply by email very soon."}</p>
      </div>
    );

  return (
    <form onSubmit={submit} data-testid={testid} className="card-surface rounded-2xl p-6 sm:p-8 space-y-4">
      <input type="text" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)}
             className="hidden" aria-hidden="true" />
      <div className="grid sm:grid-cols-2 gap-4">
        {fields.map((f) => (
          <div key={f.name} className={f.full || f.type === "textarea" ? "sm:col-span-2" : ""}>
            <label className="block text-xs uppercase tracking-wider text-white/50 mb-1.5">{f.label}{f.required && " *"}</label>
            {f.type === "textarea" ? (
              <textarea
                required={f.required} rows={4} placeholder={f.placeholder}
                data-testid={`${testid}-${f.name}`}
                value={values[f.name] || ""} onChange={(e) => set(f.name, e.target.value)}
                className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2.5 text-sm text-white outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]"
              />
            ) : f.type === "select" ? (
              <select
                required={f.required} data-testid={`${testid}-${f.name}`}
                value={values[f.name] || ""} onChange={(e) => set(f.name, e.target.value)}
                className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2.5 text-sm text-white outline-none focus:border-[#D4AF37]"
              >
                <option value="">Select...</option>
                {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <input
                type={f.type || "text"} required={f.required} placeholder={f.placeholder}
                data-testid={`${testid}-${f.name}`}
                value={values[f.name] || ""} onChange={(e) => set(f.name, e.target.value)}
                className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2.5 text-sm text-white outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]"
              />
            )}
          </div>
        ))}
      </div>
      <GoldButton type="submit" disabled={loading} data-testid={`${testid}-submit`} className="w-full sm:w-auto">
        {loading ? "Sending..." : submitLabel}
      </GoldButton>
    </form>
  );
}
